import React, { useState } from 'react';
import { 
    LineChart, Line, BarChart, Bar, PieChart, Pie, Cell,
    XAxis, YAxis, CartesianGrid, Tooltip, Legend,
    ResponsiveContainer, Area, AreaChart, ComposedChart
} from 'recharts';
import {
    TrendingUp, TrendingDown, DollarSign, AlertCircle,
    FileText, X, ChevronRight
} from 'lucide-react';
import Footer from './Footer';
import Header from './Header'

const FinancialDashboard = () => {
    const [selectedView, setSelectedView] = useState('overview');
    const [showModal, setShowModal] = useState(false);
    const [modalData, setModalData] = useState(null);

    // Cash Flow Data
    const cashFlowData = [
        { month: 'Oct 25', income: 105, expenses: 20, odBalance: -168, pcBalance: 280, netFlow: 85 },
        { month: 'Nov 25', income: 449.48, expenses: 551, odBalance: -269.52, pcBalance: 214.75, netFlow: -101.52 },
        { month: 'Dec 25', income: 487.56, expenses: 409, odBalance: -190.96, pcBalance: 149.5, netFlow: 78.56 },
        { month: 'Jan 26', income: 446, expenses: 416, odBalance: -160.96, pcBalance: 84.25, netFlow: 30 },
        { month: 'Feb 26', income: 446, expenses: 416, odBalance: -130.96, pcBalance: 19, netFlow: 30 },
        { month: 'Mar 26', income: 446, expenses: 416, odBalance: -100.96, pcBalance: -46.25, netFlow: 30 }
    ];

    // Detailed Payables
    const payablesData = [
        { date: '2025-09-02', refNo: 'RTS/1153/25-23', party: 'ROYAL TOOLS SOLUTIONS', amount: 207916, month: 'Nov 25' },
        { date: '2025-09-02', refNo: '2941', party: 'A.G.M.REXINE CENTRE', amount: 24000, month: 'Nov 25' },
        { date: '2025-09-02', refNo: 'IR000703', party: 'SRI SENTHIL TRADERS', amount: 20399, month: 'Nov 25' },
        { date: '2025-09-04', refNo: '201', party: 'CLENEX MUSFARA PINE', amount: 17400, month: 'Dec 25' },
        { date: '2025-09-10', refNo: 'A2500000838', party: 'SRI SARAVANA ASSOCIATES', amount: 17065, month: 'Dec 25' },
        { date: '2025-09-11', refNo: 'PRE25260965', party: 'PRABHA ENTERPRISES', amount: 65077, month: 'Dec 25' },
        { date: '2025-09-12', refNo: 'SAL/023/25-26', party: 'SWATHI TOOLS AND SAWS', amount: 74799, month: 'Dec 25' },
        { date: '2025-09-13', refNo: '2025-26/0693', party: 'INDUSTRIAL PACKERS', amount: 90808, month: 'Dec 25' }
    ];

    const rmPayables = [
        { date: '2025-08-12', refNo: '2502004040', party: 'JINDAL ALUMINIUM LTD', amount: 544061, month: 'Nov 25' },
        { date: '2025-08-12', refNo: '2501007430', party: 'JINDAL ALUMINIUM LTD', amount: 354896, month: 'Dec 25' },
        { date: '2025-09-10', refNo: 'MIF/25-26/286', party: 'MARVEL INDUSTRIES', amount: 3780849, month: 'Dec 25' },
        { date: '2025-09-22', refNo: '2501009679', party: 'JINDAL ALUMINIUM LTD', amount: 1801009, month: 'Dec 25' }
    ];

    const incomeData = [
        { date: 'Dec 25', source: 'Schneider Payments', amount: 33200000, type: 'Sales' },
        { date: 'Dec 25', source: 'IGST Refund', amount: 500000, type: 'Tax Refund' },
        { date: 'Dec 25', source: 'Scrap Sales', amount: 1000000, type: 'Other Income' },
        { date: 'Dec 25', source: 'Receivables', amount: 6500000, type: 'Collections' }
    ];

    const odBreakdown = [
        { creditor: 'CARGOPLAN INTERNATIONAL', amount: 4147394, category: 'Logistics' },
        { creditor: 'ALPHA CONTAINERS', amount: 132822, category: 'Packaging' },
        { creditor: 'ANMAN AIRCON SERVICE', amount: 65274, category: 'Services' },
        { creditor: 'Other Creditors', amount: 8500000, category: 'Mixed' }
    ];

    const bankingData = [
        { month: 'Oct 25', od: 260, pc: 159, bds: 600 },
        { month: 'Nov 25', od: 265, pc: 78, bds: 600 },
        { month: 'Dec 25', od: 265, pc: 0, bds: 600 },
        { month: 'Jan 26', od: 180, pc: 0, bds: 600 }
    ];

    const formatCurrency = (value) => {
        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR',
            minimumFractionDigits: 0
        }).format(value);
    };

    const handleDrilldown = (type, title) => {
        let data = [];
        if (type === 'od') data = odBreakdown;
        else if (type === 'payables') data = payablesData.filter(p => p.month === 'Dec 25');
        else if (type === 'rm') data = rmPayables.filter(p => p.month === 'Dec 25');
        else if (type === 'income') data = incomeData;
        
        setModalData({ type, title, data });
        setShowModal(true);
    };

    const metrics = [
        {
            title: 'OD Balance',
            value: '₹190.96 L',
            change: '-28.9%',
            trend: 'up',
            icon: AlertCircle,
            color: 'red',
            onClick: () => handleDrilldown('od', 'Overdraft Balance - Major Creditors')
        },
        {
            title: 'Packing Credit',
            value: '₹149.50 L',
            change: '-30.4%',
            trend: 'down',
            icon: DollarSign,
            color: 'blue',
            onClick: null
        },
        {
            title: 'Dec Payables',
            value: '₹11,743 L',
            change: '+37.7%',
            trend: 'up',
            icon: FileText,
            color: 'orange',
            onClick: () => handleDrilldown('payables', 'December Payables')
        },
        {
            title: 'Net Cash Flow',
            value: '₹78.56 L',
            change: 'Positive',
            trend: 'up',
            icon: TrendingUp,
            color: 'green',
            onClick: () => handleDrilldown('income', 'December Income')
        }
    ];

    return (
        <>
        <Header/>
        <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 p-6">
            <div className="max-w-7xl mx-auto">
                {/* Header */}
                <div className="mb-8">
                    <h1 className="text-4xl font-bold text-slate-800 mb-2">Financial Dashboard</h1>
                    <p className="text-slate-600 mb-4">Cash Flow, Receivables & Payables</p>
                    
                    <div className="flex gap-3">
                        {['overview', 'cashflow', 'payables'].map(view => (
                            <button
                                key={view}
                                onClick={() => setSelectedView(view)}
                                className={`px-4 py-2 rounded-lg font-medium ${
                                    selectedView === view
                                        ? 'bg-blue-600 text-white shadow-lg'
                                        : 'bg-white text-slate-600 hover:bg-slate-100'
                                }`}
                            >
                                {view.charAt(0).toUpperCase() + view.slice(1)}
                            </button>
                        ))}
                    </div>
                </div>

                {/* Metrics - All Clickable */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                    {metrics.map((metric, idx) => {
                        const Icon = metric.icon;
                        return (
                            <div
                                key={idx}
                                onClick={metric.onClick}
                                className={`bg-white rounded-xl shadow-lg p-6 transition-all ${
                                    metric.onClick ? 'cursor-pointer hover:shadow-2xl hover:scale-105' : ''
                                }`}
                            >
                                <div className="flex items-start justify-between mb-4">
                                    <div className={`p-3 rounded-lg bg-${metric.color}-100`}>
                                        <Icon className={`w-6 h-6 text-${metric.color}-600`} />
                                    </div>
                                    <div className={`px-2 py-1 rounded-full text-xs font-medium ${
                                        metric.trend === 'up' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                                    }`}>
                                        {metric.change}
                                    </div>
                                </div>
                                <h3 className="text-slate-600 text-sm font-medium mb-1">{metric.title}</h3>
                                <div className="flex items-end justify-between">
                                    <p className="text-2xl font-bold text-slate-800">{metric.value}</p>
                                    {metric.onClick && <ChevronRight className="w-5 h-5 text-slate-400" />}
                                </div>
                            </div>
                        );
                    })}
                </div>

                {/* Content Views */}
                {selectedView === 'overview' && (
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        <div className="bg-white rounded-xl shadow-lg p-6">
                            <h2 className="text-xl font-bold mb-4">Cash Flow</h2>
                            <ResponsiveContainer width="100%" height={300}>
                                <AreaChart data={cashFlowData}>
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <XAxis dataKey="month" />
                                    <YAxis />
                                    <Tooltip />
                                    <Area type="monotone" dataKey="income" stroke="#10b981" fill="#10b981" fillOpacity={0.3} />
                                    <Area type="monotone" dataKey="expenses" stroke="#ef4444" fill="#ef4444" fillOpacity={0.3} />
                                </AreaChart>
                            </ResponsiveContainer>
                        </div>

                        <div className="bg-white rounded-xl shadow-lg p-6">
                            <h2 className="text-xl font-bold mb-4">Banking Facilities</h2>
                            <ResponsiveContainer width="100%" height={300}>
                                <BarChart data={bankingData}>
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <XAxis dataKey="month" />
                                    <YAxis />
                                    <Tooltip />
                                    <Bar dataKey="od" fill="#ef4444" />
                                    <Bar dataKey="pc" fill="#3b82f6" />
                                    <Bar dataKey="bds" fill="#10b981" />
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                    </div>
                )}

                {selectedView === 'cashflow' && (
                    <div className="bg-white rounded-xl shadow-lg p-6">
                        <h2 className="text-2xl font-bold mb-6">Cash Flow Analysis</h2>
                        <table className="w-full">
                            <thead className="bg-slate-100">
                                <tr>
                                    <th className="px-4 py-3 text-left">Month</th>
                                    <th className="px-4 py-3 text-right">Income</th>
                                    <th className="px-4 py-3 text-right">Expenses</th>
                                    <th className="px-4 py-3 text-right">Net Flow</th>
                                </tr>
                            </thead>
                            <tbody>
                                {cashFlowData.map((row, idx) => (
                                    <tr key={idx} className="border-b hover:bg-slate-50">
                                        <td className="px-4 py-3">{row.month}</td>
                                        <td className="px-4 py-3 text-right text-green-600">₹{row.income}L</td>
                                        <td className="px-4 py-3 text-right text-red-600">₹{row.expenses}L</td>
                                        <td className={`px-4 py-3 text-right font-bold ${row.netFlow >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                            ₹{row.netFlow}L
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}

                {selectedView === 'payables' && (
                    <div className="space-y-6">
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div 
                                onClick={() => handleDrilldown('payables', '60 Days Payables')}
                                className="bg-blue-100 rounded-lg p-4 cursor-pointer hover:shadow-lg"
                            >
                                <p className="text-sm font-medium text-blue-700">60 Days</p>
                                <p className="text-2xl font-bold text-blue-900">₹5,165L</p>
                            </div>
                            <div 
                                onClick={() => handleDrilldown('rm', 'RM Payables')}
                                className="bg-purple-100 rounded-lg p-4 cursor-pointer hover:shadow-lg"
                            >
                                <p className="text-sm font-medium text-purple-700">Raw Material</p>
                                <p className="text-2xl font-bold text-purple-900">₹6,569L</p>
                            </div>
                            <div className="bg-green-100 rounded-lg p-4">
                                <p className="text-sm font-medium text-green-700">45 Days</p>
                                <p className="text-2xl font-bold text-green-900">₹563L</p>
                            </div>
                        </div>
                    </div>
                )}

                {/* Modal */}
                {showModal && modalData && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                        <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[80vh] overflow-hidden">
                            <div className="bg-blue-600 text-white p-6 flex justify-between items-center">
                                <h2 className="text-2xl font-bold">{modalData.title}</h2>
                                <button onClick={() => setShowModal(false)}>
                                    <X className="w-6 h-6" />
                                </button>
                            </div>
                            <div className="p-6 overflow-y-auto">
                                <table className="w-full">
                                    <thead className="bg-slate-100">
                                        <tr>
                                            {modalData.type === 'od' ? (
                                                <>
                                                    <th className="px-4 py-3 text-left">Creditor</th>
                                                    <th className="px-4 py-3 text-left">Category</th>
                                                    <th className="px-4 py-3 text-right">Amount</th>
                                                </>
                                            ) : modalData.type === 'income' ? (
                                                <>
                                                    <th className="px-4 py-3 text-left">Source</th>
                                                    <th className="px-4 py-3 text-left">Type</th>
                                                    <th className="px-4 py-3 text-right">Amount</th>
                                                </>
                                            ) : (
                                                <>
                                                    <th className="px-4 py-3 text-left">Date</th>
                                                    <th className="px-4 py-3 text-left">Ref</th>
                                                    <th className="px-4 py-3 text-left">Party</th>
                                                    <th className="px-4 py-3 text-right">Amount</th>
                                                </>
                                            )}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {modalData.data.map((item, idx) => (
                                            <tr key={idx} className="border-b">
                                                {modalData.type === 'od' ? (
                                                    <>
                                                        <td className="px-4 py-3">{item.creditor}</td>
                                                        <td className="px-4 py-3">{item.category}</td>
                                                        <td className="px-4 py-3 text-right font-bold">{formatCurrency(item.amount)}</td>
                                                    </>
                                                ) : modalData.type === 'income' ? (
                                                    <>
                                                        <td className="px-4 py-3">{item.source}</td>
                                                        <td className="px-4 py-3">{item.type}</td>
                                                        <td className="px-4 py-3 text-right font-bold">{formatCurrency(item.amount)}</td>
                                                    </>
                                                ) : (
                                                    <>
                                                        <td className="px-4 py-3">{item.date}</td>
                                                        <td className="px-4 py-3 font-mono text-xs">{item.refNo}</td>
                                                        <td className="px-4 py-3">{item.party}</td>
                                                        <td className="px-4 py-3 text-right font-bold">{formatCurrency(item.amount)}</td>
                                                    </>
                                                )}
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
        <Footer/>
        </> 
    );
};

export default FinancialDashboard;
